import React, { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Container, Row } from '@everlywell/leaves';
import { UserContext } from '../../Layout';

import { addToCart, addToCartDataLayer } from 'utils/cartHelpers';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ProductDataType } from 'utils/types';
import { useMembershipProducts } from 'utils/hooks';
import { PromoCard, PromoProps } from './PromoCard';

import * as S from './styles';
import Img from 'gatsby-image';
import { generateKey, logError } from 'utils/helpers';
import { filterPromosForMemberships } from '../../../utils/helpers/membershipHelpers';

export type ProductDisplayGridProps = {
  products: ProductDataType[];
  visiblePromos?: PromoProps[];
};

export const ProductDisplayGrid: React.FC<ProductDisplayGridProps> = ({ products, visiblePromos }) => {
  const userData = useContext(UserContext);
  const productsWithMembership = useMembershipProducts(userData, products);
  const [itemsInCart, setItemsInCart] = useState<Array<string>>([]);
  const [sortedProducts, setSortedProducts] = useState<Array<ProductDataType>>(
    [],
  );
  const [promos, setPromos] = useState<PromoProps[] | null>(null);

  const handleCardClick = (slug: string) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.PIP_PRODUCT_CARD,
        slug,
      },
    });
  };

  const handleAddToCartClick = (
    slug: string,
    variant: ProductDataType,
    productId: number,
  ) => {
    setItemsInCart([...itemsInCart, slug]);

    // Remove 'added' UI state
    setTimeout(
      () => setItemsInCart(itemsInCart.filter((x) => x !== slug)),
      1200,
    );

    const eventId = uuidv4();

    addToCart(variant.variantId, undefined, undefined, undefined, eventId);
    addToCartGTM(variant, productId, eventId);
  };

  const addToCartGTM = (variant: ProductDataType, productId: number, eventId: string) => {
    if (!window || !window.dataLayer || !variant) {
      return;
    }

    try {
      addToCartDataLayer(productId, variant.name, String(variant.price), eventId);
    } catch (err) {
      logError(
        'There was an error when attempting to add the addToCart event to GTM',
        {
          component: 'ProductDisplayGrid',
          function: 'addToCartGTM',
          productId,
          variant: variant.name,
          price: variant.price,
        },
      );
    }
  };

  useEffect(() => {
    if (visiblePromos && visiblePromos.length > 0) {
      setPromos(visiblePromos.filter(promo =>
        filterPromosForMemberships(
          promo.active,
          promo.slug,
          userData.membershipState,
        )));
    }
  }, [visiblePromos]);

  useEffect(() => {
    productsWithMembership.length > 0 &&
    setSortedProducts(productsWithMembership);
    if (visiblePromos && visiblePromos.length > 0) {
      setPromos(visiblePromos.filter(promo =>
        filterPromosForMemberships(
          promo.active,
          promo.slug,
          userData.membershipState,
        )));
    }
  }, [productsWithMembership, userData]);

  return (
    <div
      role='region'
      id='productGrid'
      data-testid='productGrid'
      aria-live='polite'
    >
      <Container>
        <Row>
          <S.Col xs={12}>
            {promos?.map((promo) => (
              <PromoCard key={generateKey()} {...promo} />
            ))}
            {sortedProducts.map((product: any, i: number) => {
              if (!product) return;

              return (
                <S.CardWrapper
                  key={`pip-product-card-${i}`}
                  onClick={() => handleCardClick(product.slug)}
                >
                  <S.ProductCard
                    image={
                      product.productCardImage ? (
                        <Img
                          fluid={product.productCardImage.fluid}
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <Img
                          fluid={product.pdpImage.fluid}
                          style={{ width: '100%' }}
                        />
                      )
                    }
                    imageAlt={`${product.displayName} box image`}
                    productName={product.displayName}
                    productUrl={`/products/${product.slug}/`}
                    price={`$${(+product.price).toFixed(0)}`}
                    //@ts-ignore
                    promoPrice={product.memberPrice}
                    promoText={product.promoText}
                    reviewData={null}
                    onAddToCartClick={() =>
                      handleAddToCartClick(
                        product.slug,
                        product,
                        product.productId,
                      )
                    }
                    showAddToCart={
                      !product.checkAvailability && !product.activeCredits
                    }
                    addedToCart={itemsInCart.includes(product.slug)}
                    onRemoveFromCartClick={() => null}
                    removedFromCart={!itemsInCart.includes(product.slug)}
                    isOutOfStock={product.outOfStock}
                    isOnSale={product.onSale}
                  />
                </S.CardWrapper>
              );
            })}
          </S.Col>
        </Row>
      </Container>
    </div>
  );
};

export default ProductDisplayGrid;
